import { toValue } from 'vue'

const toErrorItem = (item) =>
  typeof item === 'string' ? item : (item?.message ?? JSON.stringify(item))

/**
 * Onde a mensagem tratada pode estar no payload do backend, em ordem de
 * precedência. Tabela em vez de cadeia de `if` para manter a função dentro do
 * limite de complexidade do ESLint.
 */
const ERROR_MESSAGE_RESOLVERS = [
  (data) => data.mensagem,
  (data) => data.message,
  (data) => data.error_description,
  (data) => (Array.isArray(data.mensagens) ? data.mensagens.join('\n') : null),
  (data) => (Array.isArray(data.errors) ? data.errors.map(toErrorItem).join('\n') : null),
  (data) => data.Error, // legado: { Error: ex.Message }
  (data) => data.title || data.detail
]

export function extractErrorMessage(error) {
  const data = error?.response?.data
  if (!data) {
    return error?.message ?? 'Erro desconhecido'
  }

  if (typeof data === 'string') {
    return data // legado: string crua
  }

  for (const resolve of ERROR_MESSAGE_RESOLVERS) {
    const message = resolve(data)
    if (message) {
      return message
    }
  }

  return 'Erro desconhecido'
}

/**
 * Chaves com serialização própria em `buildQueryParams` — restante do objeto
 * de opções vira parâmetro escalar com o próprio nome.
 */
const RESERVED_KEYS = ['columns', 'filters', 'sortBy']

const isEmpty = (value) => value === null || value === undefined || value === ''

const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Anexa um valor único. Array vira chave repetida (`ids=1&ids=2`) e objeto vira
 * notação de ponto (`periodo.de=...`), que é o formato que o model binder do
 * ASP.NET espera. `null`, `undefined` e `''` são descartados — mandar
 * `situacao=null` faz o backend receber a string "null".
 */
function appendValue(params, key, rawValue) {
  const value = toValue(rawValue)

  if (isEmpty(value)) {
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendValue(params, key, item))
    return
  }

  if (isPlainObject(value)) {
    Object.keys(value).forEach((subKey) => appendValue(params, `${key}.${subKey}`, value[subKey]))
    return
  }

  params.append(key, value)
}

/** `filters[0].campo=x`, `sortBy[0].direction=asc`, `columns[0]=numero`. */
function appendIndexed(params, name, list) {
  const items = toValue(list) ?? []
  items.forEach((item, index) => appendValue(params, `${name}[${index}]`, item))
}

/**
 * Colunas que vão para o servidor: descarta as escondidas e a coluna de ações,
 * sem mutar as definições recebidas (elas costumam vir de props reativas do
 * `<data-grid>`). Aceita tanto `{ value, visible }` quanto string.
 */
export function visibleColumnValues(columns) {
  return (toValue(columns) ?? [])
    .map((column) => (typeof column === 'string' ? { value: column } : toValue(column)))
    .filter((column) => column?.visible !== false)
    .map((column) => column?.value)
    .filter((value) => !isEmpty(value) && value !== 'acoes')
}

/**
 * Monta a query string do padrão de listagem/exportação da API a partir de um
 * objeto de opções. Devolve um `URLSearchParams`, que é o que o axios aceita
 * direto em `{ params }`.
 *
 * `columns`, `filters` e `sortBy` têm serialização indexada; qualquer outra
 * chave é anexada como parâmetro comum, então escalar novo (`situacao`,
 * `responsavelId`, `tag`…) não pede alteração aqui. Refs e getters são
 * desembrulhados, então dá para chamar de dentro de um componente sem `.value`.
 *
 * @param {Object} [options]
 * @param {Array<{value: string, visible?: boolean}|string>} [options.columns] serializado como `columns[i]`
 * @param {Array<Object>} [options.filters] serializado como `filters[i].chave`
 * @param {Array<Object>} [options.sortBy] serializado como `sortBy[i].chave`
 * @returns {URLSearchParams}
 *
 * @example
 * const params = buildQueryParams({
 *   pageNumber: 1,
 *   pageSize: 20,
 *   searchTerm,
 *   situacao: 'EM_ANDAMENTO',
 *   filters,
 *   sortBy,
 *   columns
 * })
 * const { data } = await Httpservice.getAxiosClient().get(`${API_URL}/api/processo`, { params })
 */
export function buildQueryParams(options = {}) {
  const params = new URLSearchParams()

  appendIndexed(params, 'columns', visibleColumnValues(options.columns))

  Object.keys(options)
    .filter((key) => !RESERVED_KEYS.includes(key))
    .forEach((key) => appendValue(params, key, options[key]))

  appendIndexed(params, 'filters', options.filters)
  appendIndexed(params, 'sortBy', options.sortBy)

  return params
}
